import { useEffect, useState } from 'react';
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import { getFileName } from '../../util/util';
import { captureSupabaseError } from '../../lib/sentry';

interface FileAnswerProps {
  value?: string; // storage path (not a full URL)
  onChange: (val?: File | string) => void; // File object when uploading, undefined when removing
  required?: boolean;
  savingAnswer?: boolean;
  error?: boolean;
}

export default function FileAnswer({
  value,
  onChange,
  required = false,
  savingAnswer = false,
  error = false,
}: FileAnswerProps) {
  const [localValue, setLocalValue] = useState<string | undefined>(value);
  const [generatingLink, setGeneratingLink] = useState(false);
  const invalidClass = error ? 'is-invalid' : '';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLocalValue(undefined); // clear current file display
    onChange(file);
  };

  const handleRemove = () => {
    if (savingAnswer) {
      // If we're saving, we can't remove the file yet
      return;
    }
    setLocalValue(undefined);
    onChange(undefined); // notify parent to remove file
  };

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!value) return;

    try {
      setGeneratingLink(true);

      const { data, error } = await supabase.storage
        .from('assessment-answers')
        .createSignedUrl(value, 60 * 60); // valid for 1 hour

      if (error) {
        captureSupabaseError(error, { op: 'storage.createSignedUrl' });
        console.error('Error creating signed URL:', error);
        alert('Failed to generate download link.');
        return;
      }

      // Open signed link in new tab
      window.open(data.signedUrl, '_blank');
    } finally {
      setGeneratingLink(false);
    }
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="mb-3">
      {localValue ? (
        <div>
          <p>
            Uploaded file:{' '}
            {/* <span className="text-muted">{getFileName(localValue)}</span> */}
          </p>
          {generatingLink ? (
            <>
              <Spinner animation="border" size="sm" /> Downloading...
            </>
          ) : (
            <ListGroup horizontal className="my-2">
              <ListGroup.Item>
                <Button
                  variant="link"
                  onClick={handleDownloadClick}
                  disabled={generatingLink}
                >
                  <i className="bi bi-cloud-download me-2" />{' '}
                  {getFileName(localValue)}
                </Button>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex align-items-center">
                {savingAnswer ? (
                  <i className="bi bi-check-circle"></i>
                ) : (
                  <i
                    onClick={handleRemove}
                    className="bi bi-x-circle text-danger"
                    style={{ cursor: 'pointer' }}
                    title="Remove file"
                  ></i>
                )}
              </ListGroup.Item>
            </ListGroup>
          )}
        </div>
      ) : (
        <Form.Control
          type="file"
          className={invalidClass}
          onChange={handleFileChange}
          required={required}
          disabled={savingAnswer}
        />
      )}
    </div>
  );
}
